import React, { useState, useEffect, useContext, useRef } from "react";
import "emoji-mart/css/emoji-mart.css";
import { useParams } from "react-router-dom";
import { Picker } from "emoji-mart";
import clsx from "clsx";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import Paper from "@material-ui/core/Paper";
import InputBase from "@material-ui/core/InputBase";
import CircularProgress from "@material-ui/core/CircularProgress";
import { green } from "@material-ui/core/colors";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import IconButton from "@material-ui/core/IconButton";
import MoreVert from "@material-ui/icons/MoreVert";
import MoodIcon from "@material-ui/icons/Mood";
import SendIcon from "@material-ui/icons/Send";
import CancelIcon from "@material-ui/icons/Cancel";
import ClearIcon from "@material-ui/icons/Clear";
import MicIcon from "@material-ui/icons/Mic";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import HighlightOffIcon from "@material-ui/icons/HighlightOff";
import {
  FormControlLabel,
  Hidden,
  Menu,
  MenuItem,
  Switch,
} from "@material-ui/core";
import ClickAwayListener from "@material-ui/core/ClickAwayListener";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import RecordingTimer from "./RecordingTimer";
import { ReplyMessageContext } from "../../context/ReplyingMessage/ReplyingMessageContext";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import toastError from "../../errors/toastError";

// Native audio recorder using MediaRecorder API
class NativeAudioRecorder {
  constructor() {
    this.mediaRecorder = null;
    this.audioChunks = [];
    this.stream = null;
  }

  async start() {
    try {
      console.log("[Audio] Starting native recorder...");
      this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // Use webm format with opus codec (widely supported)
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : 'audio/webm';

      this.mediaRecorder = new MediaRecorder(this.stream, { mimeType });
      this.audioChunks = [];

      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.audioChunks.push(event.data);
        }
      };

      this.mediaRecorder.start();
      console.log("[Audio] Recording started successfully");
      return Promise.resolve();
    } catch (error) {
      console.error("[Audio] Failed to start recording:", error);
      return Promise.reject(error);
    }
  }

  async stop() {
    return new Promise((resolve, reject) => {
      if (!this.mediaRecorder || this.mediaRecorder.state === 'inactive') {
        reject(new Error('Recorder not active'));
        return;
      }

      this.mediaRecorder.onstop = () => {
        console.log("[Audio] Recording stopped");
        const audioBlob = new Blob(this.audioChunks, { type: this.mediaRecorder.mimeType });

        // Stop all tracks
        if (this.stream) {
          this.stream.getTracks().forEach(track => track.stop());
        }

        // Convert to File object with .webm extension
        const audioFile = new File([audioBlob], 'audio.webm', {
          type: this.mediaRecorder.mimeType,
          lastModified: Date.now()
        });

        resolve([audioFile, audioBlob]);
      };

      this.mediaRecorder.onerror = (error) => {
        console.error("[Audio] Recording error:", error);
        reject(error);
      };

      this.mediaRecorder.stop();
    });
  }
}

let audioRecorder = null;

const initRecorder = async () => {
  if (!audioRecorder) {
    audioRecorder = new NativeAudioRecorder();
    console.log("[Audio] Native recorder initialized (v11.18)");
  }
  return audioRecorder;
};

const useStyles = makeStyles(theme => ({
  mainWrapper: {
    background: "#eee",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    borderTop: "1px solid rgba(0, 0, 0, 0.12)",
    [theme.breakpoints.down("sm")]: {
      position: "fixed",
      bottom: 0,
      width: "100%",
    },
  },

  newMessageBox: {
    background: "#eee",
    width: "100%",
    display: "flex",
    padding: "7px",
    alignItems: "center",
  },

  messageInputWrapper: {
    padding: 6,
    marginRight: 7,
    background: "#fff",
    display: "flex",
    borderRadius: 20,
    flex: 1,
    position: "relative",
  },

  messageInput: {
    paddingLeft: 10,
    flex: 1,
    border: "none",
  },

  sendMessageIcons: {
    color: "grey",
  },

  uploadInput: {
    display: "none",
  },

  viewMediaInputWrapper: {
    display: "flex",
    padding: "10px 13px",
    position: "relative",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: "#eee",
    borderTop: "1px solid rgba(0, 0, 0, 0.12)",
  },

  emojiBox: {
    position: "absolute",
    bottom: 63,
    width: 40,
    borderTop: "1px solid #e8e8e8",
  },

  circleLoading: {
    color: green[500],
    opacity: "70%",
    position: "absolute",
    top: "20%",
    left: "50%",
    marginLeft: -12,
  },

  audioLoading: {
    color: green[500],
    opacity: "70%",
  },

  recorderWrapper: {
    display: "flex",
    alignItems: "center",
    alignContent: "middle",
  },

  cancelAudioIcon: {
    color: "red",
  },

  sendAudioIcon: {
    color: "green",
  },

  replyginMsgWrapper: {
    display: "flex",
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
    paddingTop: 8,
    paddingLeft: 73,
    paddingRight: 7,
  },

  replyginMsgContainer: {
    flex: 1,
    marginRight: 5,
    overflowY: "hidden",
    backgroundColor: "rgba(0, 0, 0, 0.05)",
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
  },

  replyginMsgBody: {
    padding: 10,
    height: "auto",
    display: "block",
    whiteSpace: "pre-wrap",
    overflow: "hidden",
  },

  replyginContactMsgSideColor: {
    flex: "none",
    width: "4px",
    backgroundColor: "#35cd96",
  },

  replyginSelfMsgSideColor: {
    flex: "none",
    width: "4px",
    backgroundColor: "#6bcbef",
  },

  messageContactName: {
    display: "flex",
    color: "#6bcbef",
    fontWeight: 500,
  },
  messageQuickAnswersWrapper: {
    margin: 0,
    position: "absolute",
    bottom: "50px",
    background: "#ffffff",
    padding: "2px",
    border: "1px solid #CCC",
    left: 0,
    width: "100%",
    "& li": {
      listStyle: "none",
      "& a": {
        display: "block",
        padding: "8px",
        textOverflow: "ellipsis",
        overflow: "hidden",
        maxHeight: "32px",
        "&:hover": {
          background: "#F1F1F1",
          cursor: "pointer",
        },
      },
    },
  },
}));

const MessageInput = ({ ticketStatus }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { ticketId } = useParams();

  const [medias, setMedias] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [showEmoji, setShowEmoji] = useState(false);
  const [loading, setLoading] = useState(false);
  const [recording, setRecording] = useState(false);
  const [quickAnswers, setQuickAnswer] = useState([]);
  const [typeBar, setTypeBar] = useState(false);
  const inputRef = useRef();
  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState(null);
  const { setReplyingMessage, replyingMessage } =
    useContext(ReplyMessageContext);
  const { user } = useContext(AuthContext);

  const [signMessage, setSignMessage] = useLocalStorage("signOption", true);

  useEffect(() => {
    inputRef.current.focus();
  }, [replyingMessage]);

  useEffect(() => {
    inputRef.current.focus();
    return () => {
      setInputMessage("");
      setShowEmoji(false);
      setMedias([]);
      setReplyingMessage(null);
    };
  }, [ticketId, setReplyingMessage]);

  const handleChangeInput = e => {
    setInputMessage(e.target.value);
    handleLoadQuickAnswer(e.target.value);
  };

  const handleQuickAnswersClick = value => {
    setInputMessage(value);
    setTypeBar(false);
  };

  const handleLoadQuickAnswer = async value => {
    if (value && value.indexOf("/") === 0) {
      try {
        const { data } = await api.get("/quickAnswers/", {
          params: { searchParam: inputMessage.substring(1) },
        });
        setQuickAnswer(data.quickAnswers);
        if (data.quickAnswers.length > 0) {
          setTypeBar(true);
        } else {
          setTypeBar(false);
        }
      } catch (err) {
        setTypeBar(false);
      }
    } else {
      setTypeBar(false);
    }
  };

  const handleAddEmoji = e => {
    let emoji = e.native;
    setInputMessage(prevState => prevState + emoji);
  };

  const handleChangeMedias = e => {
    if (!e.target.files) {
      return;
    }

    const selectedMedias = Array.from(e.target.files);
    setMedias(selectedMedias);
  };

  const handleInputPaste = e => {
    if (e.clipboardData.files[0]) {
      setMedias([e.clipboardData.files[0]]);
    }
  };

  const handleUploadMedia = async e => {
    setLoading(true);
    e.preventDefault();

    const formData = new FormData();
    formData.append("fromMe", true);
    medias.forEach(media => {
      formData.append("medias", media);
      formData.append("body", media.name);
    });

    try {
      await api.post(`/messages/${ticketId}`, formData);
    } catch (err) {
      toastError(err);
    }

    setLoading(false);
    setMedias([]);
  };

  const handleSendMessage = async () => {
    if (inputMessage.trim() === "") return;
    setLoading(true);

    const message = {
      read: 1,
      fromMe: true,
      mediaUrl: "",
      body: signMessage
        ? `*${user?.name}:*\n${inputMessage.trim()}`
        : inputMessage.trim(),
      quotedMsg: replyingMessage,
    };
    try {
      await api.post(`/messages/${ticketId}`, message);
    } catch (err) {
      toastError(err);
    }

    setInputMessage("");
    setShowEmoji(false);
    setLoading(false);
    setReplyingMessage(null);
  };

  const handleStartRecording = async () => {
    setLoading(true);
    try {
      console.log("[Audio] Starting recording process...");
      const recorder = await initRecorder();
      if (!recorder) {
        throw new Error("Recorder not available");
      }
      await recorder.start();
      setRecording(true);
      setLoading(false);
      console.log("[Audio] Recording active");
    } catch (err) {
      console.error("[Audio] Recording error:", err);
      toastError(err);
      setLoading(false);
    }
  };

  const handleUploadAudio = async () => {
    if (loading) {
      console.warn("[Audio] Already processing, ignoring click");
      return;
    }

    setLoading(true);
    setRecording(false); // Stop recording state immediately

    try {
      const recorder = await initRecorder();
      if (!recorder) {
        throw new Error("Recorder not available");
      }

      console.log("[Audio] Stopping and processing recording...");
      const [audioFile, blob] = await recorder.stop();

      if (blob.size < 10000) {
        console.warn("[Audio] Recording too short, discarding");
        setLoading(false);
        return;
      }

      const formData = new FormData();
      const filename = `${new Date().getTime()}.webm`;
      formData.append("medias", audioFile, filename);
      formData.append("body", filename);
      formData.append("fromMe", true);

      await api.post(`/messages/${ticketId}`, formData);
      console.log("[Audio] Upload successful");
    } catch (err) {
      console.error("[Audio] Upload error:", err);
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelAudio = async () => {
    try {
      console.log("[Audio] Canceling recording...");
      const recorder = await initRecorder();
      if (recorder && recorder.mediaRecorder && recorder.mediaRecorder.state !== 'inactive') {
        await recorder.stop();
      }
      setRecording(false);
      console.log("[Audio] Recording canceled");
    } catch (err) {
      console.error("[Audio] Cancel error:", err);
      toastError(err);
    }
  };

  const handleOpenMenuClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuItemClick = event => {
    setAnchorEl(null);
  };

  const renderReplyingMessage = message => {
    return (
      <div className={classes.replyginMsgWrapper}>
        <div className={classes.replyginMsgContainer}>
          <span
            className={clsx(classes.replyginContactMsgSideColor, {
              [classes.replyginSelfMsgSideColor]: !message.fromMe,
            })}
          ></span>
          <div className={classes.replyginMsgBody}>
            {!message.fromMe && (
              <span className={classes.messageContactName}>
                {message.contact?.name}
              </span>
            )}
            {message.body}
          </div>
        </div>
        <IconButton
          aria-label="showRecorder"
          component="span"
          disabled={loading || ticketStatus !== "open"}
          onClick={() => setReplyingMessage(null)}
        >
          <ClearIcon className={classes.sendMessageIcons} />
        </IconButton>
      </div>
    );
  };

  if (medias.length > 0)
    return (
      <div className="p-3 border-top bg-light">
        <div className="d-flex align-items-center justify-content-between bg-white p-2 rounded shadow-sm">
          <button className="btn btn-sm btn-action-gray btn-icon me-2" onClick={() => setMedias([])}>
            <i className="fi fi-rr-cross-small"></i>
          </button>

          <div className="flex-grow-1 text-truncate pe-3">
            {loading ? <CircularProgress size={20} /> : <span>{medias[0]?.name}</span>}
          </div>

          <button className="btn btn-primary px-3 rounded-2" onClick={handleUploadMedia} disabled={loading}>
            Enviar
          </button>
        </div>
      </div>
    );
  else {
    return (
      <div className="p-3 border-top bg-white chat-sidebar-footer">
        {replyingMessage && renderReplyingMessage(replyingMessage)}

        <div className="chat-send-form d-flex align-items-center bg-light p-2 rounded-3 position-relative">
          {/* Mobile: Botão + com Menu */}
          {isMobile && !recording && (
            <>
              <button
                type="button"
                className="btn btn-sm btn-icon waves-effect me-1"
                onClick={(e) => setMobileMenuAnchor(e.currentTarget)}
                disabled={loading || ticketStatus !== "open"}
                title="Opções"
              >
                <i className="fi fi-rr-plus text-primary" style={{ fontSize: '20px' }}></i>
              </button>

              <Menu
                anchorEl={mobileMenuAnchor}
                open={Boolean(mobileMenuAnchor)}
                onClose={() => setMobileMenuAnchor(null)}
                anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              >
                <MenuItem onClick={() => { setShowEmoji(!showEmoji); setMobileMenuAnchor(null); }}>
                  <i className="fi fi-rr-smile text-warning me-2"></i>
                  Emoji
                </MenuItem>
                <MenuItem onClick={() => { document.getElementById('upload-button').click(); setMobileMenuAnchor(null); }}>
                  <i className="fi fi-rr-clip text-info me-2"></i>
                  Anexar
                </MenuItem>
                <MenuItem onClick={() => { handleStartRecording(); setMobileMenuAnchor(null); }}>
                  <i className="fi fi-rr-microphone text-primary me-2"></i>
                  Áudio
                </MenuItem>
                <MenuItem onClick={() => { setSignMessage(!signMessage); setMobileMenuAnchor(null); }}>
                  <i className={`fi ${signMessage ? 'fi-sr-check' : 'fi-rr-check'} text-success me-2`}></i>
                  Assinatura {signMessage ? '(Ativa)' : '(Inativa)'}
                </MenuItem>
              </Menu>
            </>
          )}

          {/* Desktop: Botão Emoji */}
          {!isMobile && (
            <button
              type="button"
              className="btn btn-sm btn-icon waves-effect me-1"
              disabled={loading || recording || ticketStatus !== "open"}
              onClick={() => setShowEmoji(prevState => !prevState)}
            >
              <i className={`fi ${showEmoji ? 'fi-sr-smile' : 'fi-rr-smile'} text-warning`} style={{ fontSize: '20px' }}></i>
            </button>
          )}

          {showEmoji && (
            <div style={{ position: 'absolute', bottom: '60px', left: '0', zIndex: 1000 }}>
              <ClickAwayListener onClickAway={() => setShowEmoji(false)}>
                <Picker
                  perLine={12}
                  showPreview={false}
                  showSkinTones={false}
                  onSelect={handleAddEmoji}
                  theme="light"
                />
              </ClickAwayListener>
            </div>
          )}

          {/* Menu de Respostas Rápidas */}
          {typeBar && (
            <ul className={classes.messageQuickAnswersWrapper}>
              {quickAnswers.map((value, index) => {
                return (
                  <li key={index}>
                    {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                    <a onClick={() => handleQuickAnswersClick(value.message)}>
                      {`${value.shortcut} - ${value.message}`}
                    </a>
                  </li>
                );
              })}
            </ul>
          )}

          {/* Input de Texto */}
          <InputBase
            inputRef={(input) => {
              input && input.focus();
              input && (inputRef.current = input);
            }}
            className="form-control bg-transparent border-0 shadow-none flex-grow-1"
            placeholder={
              ticketStatus === "open"
                ? i18n.t("messagesInput.placeholderOpen")
                : i18n.t("messagesInput.placeholderClosed")
            }
            multiline
            maxRows={5}
            value={inputMessage}
            onChange={handleChangeInput}
            disabled={recording || loading || ticketStatus !== "open"}
            onPaste={(e) => {
              ticketStatus === "open" && handleInputPaste(e);
            }}
            onKeyPress={(e) => {
              if (loading || e.shiftKey) return;
              else if (e.key === "Enter") {
                handleSendMessage();
              }
            }}
          />

          <div className="d-flex align-items-center">
            {/* Desktop: Anexos (Imagem / Arquivo) */}
            <input
              multiple
              type="file"
              id="upload-button"
              disabled={loading || recording || ticketStatus !== "open"}
              style={{ display: 'none' }}
              onChange={handleChangeMedias}
            />
            {!isMobile && (
              <label
                htmlFor="upload-button"
                className="m-0"
                style={{
                  cursor: (loading || recording || ticketStatus !== "open") ? 'not-allowed' : 'pointer',
                  opacity: (loading || recording || ticketStatus !== "open") ? 0.5 : 1
                }}
              >
                <span className="btn btn-sm btn-icon waves-effect">
                  <i className="fi fi-rr-clip"></i>
                </span>
              </label>
            )}

            {/* Desktop: Assinatura (Toggle Animado) */}
            {!isMobile && (
              <div className="form-check form-switch ms-1 me-2" title="Assinatura">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={signMessage}
                  onChange={(e) => setSignMessage(e.target.checked)}
                  disabled={loading || recording || ticketStatus !== "open"}
                />
              </div>
            )}

            {/* Gravação de Áudio (Controles) */}
            {recording ? (
              <div className="d-flex align-items-center bg-white rounded-pill px-2 shadow-sm border border-danger mx-1">
                <button type="button" className="btn btn-sm btn-icon text-danger" onClick={handleCancelAudio}>
                  <i className="fi fi-rr-trash"></i>
                </button>
                <div className="px-2 text-danger fw-bold"><RecordingTimer /></div>
                <button type="button" className="btn btn-sm btn-icon text-success" onClick={handleUploadAudio}>
                  <i className="fi fi-rr-check"></i>
                </button>
              </div>
            ) : (
              !isMobile && (
                <button
                  type="button"
                  className="btn btn-sm btn-icon waves-effect me-1"
                  onClick={handleStartRecording}
                  disabled={loading || ticketStatus !== "open"}
                  title="Gravar Áudio"
                >
                  <i className="fi fi-rr-microphone text-primary" style={{ fontSize: '20px' }}></i>
                </button>
              )
            )}

            {/* Botão Enviar */}
            <button
              type="button"
              className="btn btn-primary btn-icon waves-effect waves-light shadow-sm"
              onClick={handleSendMessage}
              disabled={loading || !inputMessage.trim()}
            >
              <i className="fi fi-rr-paper-plane"></i>
            </button>
          </div>
        </div>

        {/* Atalhos Rápidos */}
        {typeBar && (
          <div className="position-absolute bottom-100 start-0 w-100 bg-white border shadow-sm rounded-2 mb-2 p-2">
            <ul className="list-unstyled mb-0">
              {quickAnswers.map((value, index) => (
                <li key={index}>
                  <button
                    className="btn btn-link btn-sm text-dark text-start w-100 text-decoration-none"
                    onClick={() => handleQuickAnswersClick(value.message)}
                  >
                    <strong>/{value.shortcut}</strong> - {value.message}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  }
};

export default MessageInput;
